/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE LANGUAGES THIS SYSTEM STORES TEXT IN. [P6b]
 *
 *  ⚠️ HERE RATHER THAN IN `pwa/src/i18n/`, AND THE MOVE IS WHAT MAKES A PER-LOCALE
 *  COLUMN POSSIBLE AT ALL. The officer app declared `LOCALES` first and was the only
 *  consumer, so it lived there; P6b stores an operator's tile-title override KEYED BY
 *  LOCALE, which puts the vocabulary in `packages/shared` — a leaf app is not importable
 *  by the schema layer, and a second copy is how the two halves come to disagree about
 *  what `'bn'` means.
 *
 *  ⚠️ `constants/` AND NOT `schema/`, FOR THE REASON `dashboardTile.constant.ts` GIVES.
 *  This module imports no zod, so `pwa` may import it inside its 150 KB budget —
 *  CLAUDE.md §8: runtime values reach that app from `@pams/shared/constants` and from
 *  nowhere else. `pwa/src/i18n/index.ts` RE-EXPORTS these rather than re-declaring them,
 *  so `i18n.test.ts` keeps reading the same tuple it always did.
 *
 *  ⚠️ ONE MEMBER TODAY, AND THAT IS NOT A REASON TO COLLAPSE IT TO A STRING.
 *  PA_REQUIREMENTS §7: "English v1, but every user-facing string goes through an i18n
 *  layer from day one. Bengali will be asked for." A stored override with no language on
 *  it is a column whose migration has to GUESS which language the rows are in, and the
 *  guess is unverifiable after the fact. Adding Bengali stays what `pwa`'s own header
 *  promises — a file plus a line.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const LOCALES = ['en'] as const;
export type Locale = (typeof LOCALES)[number];

/**
 * ⚠️ THE FALLBACK, NOT MERELY THE INITIAL VALUE. A stored override carrying no entry for
 * the reader's locale falls back to English, and English falling back to the registry's
 * own word — never to an empty string, which is `pwa/src/i18n/index.ts`'s rule and the
 * same reason a blank override means "use the registry name" rather than "no name".
 */
export const DEFAULT_LOCALE: Locale = 'en';

/** Narrowing for anything that reads a locale off the wire or out of a stored map. */
export const isLocale = (value: string): value is Locale => (LOCALES as readonly string[]).includes(value);
